class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}
customElements.define('facet-short', FacetShort, { extends: 'select' });

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    this.minPriceInput.addEventListener('focus', this.minPriceInput.select);
    this.maxPriceInput.addEventListener('focus', this.maxPriceInput.select);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.maxPriceInput.value || event.target.max) - 1),
      event.target.min
    );
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.minPriceInput.value || event.target.min) + 1),
      event.target.max
    );
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value),
      parseInt(this.maxPriceInput.value || event.target.max) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value),
      parseInt(this.minPriceInput.value || event.target.min) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange() {
    this.dirty = true;
    this.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);

    setTimeout(() => {
      const target = document.querySelector('.collection');
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - 200,
        behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth',
      });
      if (loadings) {
        loadings.forEach((loading) => {
          loading.classList.add('btn--loading');
        });
      }
    }, 100);
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) },
      ],
    ]);

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  renderSection(url, event) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url, event) : this.renderSectionFromFetch(url, event);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  renderSectionFromFetch(url, event) {
    this.beforeRenderSection();
    const start = performance.now();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const execution = performance.now() - start;

        setTimeout(
          () => {
            this.renderFilters(responseText, event);
            this.renderFiltersActive(responseText);
            this.renderProductGridContainer(responseText);
            this.renderFacetFiltersContainer(responseText);
            this.renderProductCount(responseText);
            this.renderSortBy(responseText);
            this.renderSortByMobile(responseText);

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });
            this.cachedMap.set(url, responseText);

            this.afterRenderSection();
          },
          execution > 250 ? 0 : 250
        );
      });
  }

  renderSectionFromCache(url, event) {
    this.beforeRenderSection();

    setTimeout(() => {
      const responseText = this.cachedMap.get(url);
      this.renderFilters(responseText, event);
      this.renderFiltersActive(responseText);
      this.renderProductGridContainer(responseText);
      this.renderFacetFiltersContainer(responseText);
      this.renderProductCount(responseText);
      this.renderSortBy(responseText);
      this.renderSortByMobile(responseText);

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

      this.afterRenderSection();
    }, 250);
  }

  renderFilters(responseText, event) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const facetElements = parsedHTML.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter]'
    );

    const facetElementsFormDom = document.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter]'
    );

    Array.from(facetElementsFormDom).forEach((currentElement) => {
      if (!Array.from(facetElements).some(({ id }) => currentElement.id === id)) {
        currentElement.classList.add('hidden');
      }
    });

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    facetsToRender.forEach((element) => {
      const filter = document.querySelector(`[data-filter][data-index="${element.dataset.index}"]`);
      if (filter !== null) {
        filter.classList.remove('hidden');
        if (filter.tagName === 'DETAILS') {
          filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
        } else {
          filter.innerHTML = element.innerHTML;
        }
      }
    });
  }

  renderFiltersActive(responseText) {
    const id = 'FacetFiltersActive';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductGridContainer(responseText) {
    const id = 'ProductGridContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;

    const layoutSwitcher = document.querySelector('layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
  }

  renderFacetFiltersContainer(responseText) {
    const id = 'FacetFiltersContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductCount(responseText) {
    const id = 'ProductCount';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortBy(responseText) {
    const id = 'SortByContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortByMobile(responseText) {
    const id = 'SortByContainerMobile';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}
customElements.define('facet-remove', FacetRemove, { extends: 'a' });

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    this.innerText = facetCount && facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class FacetToggler extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.facetEl = document.getElementById('FacetFiltersContainer');
    this.addEventListener('click', this.onClickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick(evt) {
    evt.preventDefault();

    if (this.facetEl.classList.contains('xl:block')) {
      this.facetEl.classList.remove('xl:block');
    } else {
      this.facetEl.classList.add('xl:block');
    }
  }
}
customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') == 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return;
    this.loadingState();

    const url = this.setUrl().toString();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderPagination(responseText);
        this.renderProductGridContainer(responseText);
      });
  }

  renderPagination(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const pagination = productGridContainer.querySelector('.pagination');
    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productList = productGridContainer.querySelector('.products-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.f-column').forEach((item) => {
        productList.appendChild(item);
      });
    }
  }

  setUrl() {
    const url = new URL(this.getAttribute('action'));
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }
}
customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'hypertheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  /**
   * @param {event|element} evt
   */
  onButtonClick(evt) {
    const target = evt.currentTarget ? evt.currentTarget : evt;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);
        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;

    productGrid.dataset.layout = layoutMode;

    const { gridClass, listClass } = productGrid.dataset;
    if ((gridClass || listClass) && gridClass !== listClass) {
      const productCards = productGrid.querySelectorAll('.product-card');
      productCards &&
        productCards.forEach((card) => {
          if ('grid' === layoutMode) {
            listClass && card.classList.remove(listClass);
            gridClass && card.classList.add(gridClass);
          } else {
            gridClass && card.classList.remove(gridClass);
            listClass && card.classList.add(listClass);
          }
        });
    }

    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });
    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
customElements.define('layout-switcher', LayoutSwitcher);

(() => {
  const departmentFallbacks = {
    'on-sale': [
      ['type of product', true, [['Sofas', '/collections/on-sale/sofa'], ['Dining Tables', '/collections/on-sale/dining-table'], ['Cabinets', '/collections/on-sale/cabinet'], ['Chairs', '/collections/on-sale/chair'], ['Beds', '/collections/on-sale/bed'], ['Nightstands', '/collections/on-sale/nightstand'], ['Tables', '/collections/on-sale/table'], ['Rugs', '/collections/on-sale/rug'], ['Lighting', '/collections/on-sale/lighting'], ['Decor', '/collections/on-sale/decor'], ['Outdoor', '/collections/on-sale/outdoor']]],
      ['room', false, [['Living Room', '/collections/on-sale/living-room'], ['Dining Room', '/collections/on-sale/dining-room'], ['Bedroom', '/collections/on-sale/bedroom'], ['Outdoor', '/collections/on-sale/outdoor']]],
      ['brand', false, [['Universal', '/collections/on-sale/universal'], ['Uttermost', '/collections/on-sale/uttermost'], ['Classic Home', '/collections/on-sale/classic-home'], ['Artisan', '/collections/on-sale/dovetail'], ['Mobital', '/collections/on-sale/mobital'], ['Mercana', '/collections/on-sale/mercana']]],
      ['style', false, [['Modern', '/collections/on-sale/modern'], ['Rustic', '/collections/on-sale/rustic'], ['Coastal', '/collections/on-sale/coastal'], ['Industrial', '/collections/on-sale/industrial'], ['Traditional', '/collections/on-sale/traditional']]],
    ],
    furniture: [
      ['type of product', true, [['Sofas', '/collections/furniture/sofas'], ['Sectionals', '/collections/furniture/sectional-sofas'], ['Accent Chairs', '/collections/furniture/chairs'], ['Dining Tables', '/collections/furniture/dining-table'], ['Dining Chairs', '/collections/furniture/dining-chair'], ['Coffee Tables', '/collections/furniture/coffee-table'], ['Cabinets', '/collections/furniture/cabinet'], ['Media Consoles', '/collections/furniture/media-console'], ['Beds', '/collections/furniture/bed'], ['Nightstands', '/collections/furniture/nightstand'], ['Desks', '/collections/furniture/desk'], ['Outdoor', '/collections/outdoor-furniture']]],
      ['room', false, [['Living Room', '/collections/furniture/living-room'], ['Dining Room', '/collections/furniture/dining-room'], ['Bedroom', '/collections/furniture/bedroom'], ['Home Office', '/collections/furniture/home-office'], ['Outdoor', '/collections/outdoor-furniture']]],
      ['brand', false, [['Universal', '/collections/furniture/universal'], ['Classic Home', '/collections/furniture/classic-home'], ['Uttermost', '/collections/furniture/uttermost'], ['Artisan', '/collections/furniture/dovetail'], ['Mobital', '/collections/furniture/mobital'], ['Mercana', '/collections/furniture/mercana'], ['Four Hands', '/collections/furniture/four-hands']]],
      ['style', false, [['Modern', '/collections/furniture/modern'], ['Rustic Farmhouse', '/collections/rustic-lodge-farmhouse-furniture'], ['Industrial Loft', '/collections/the-industrial-loft'], ['Beach Cottage', '/collections/beach-cottage-furniture'], ['French Country', '/collections/french-country-furniture'], ['Small Space', '/collections/small-space-furniture']]],
    ],
    'furniture-living-room-furniture': [
      ['type of product', true, [['Sofas', '/collections/furniture-living-room-furniture/sofas'], ['Sectionals', '/collections/furniture-living-room-furniture/sectional-sofas'], ['Chairs', '/collections/furniture-living-room-furniture/chairs'], ['Coffee Tables', '/collections/furniture-living-room-furniture/coffee-table'], ['Accent Tables', '/collections/furniture-living-room-furniture/accent-table'], ['Media Consoles', '/collections/furniture-living-room-furniture/media-console'], ['Cabinets', '/collections/furniture-living-room-furniture/cabinet'], ['Ottomans', '/collections/furniture-living-room-furniture/ottoman']]],
      ['material', false, [['Leather', '/collections/furniture-living-room-furniture/leather'], ['Fabric', '/collections/furniture-living-room-furniture/fabric'], ['Wood', '/collections/furniture-living-room-furniture/wood'], ['Metal', '/collections/furniture-living-room-furniture/metal']]],
      ['brand', false, [['Universal', '/collections/furniture-living-room-furniture/universal'], ['Classic Home', '/collections/furniture-living-room-furniture/classic-home'], ['Uttermost', '/collections/furniture-living-room-furniture/uttermost'], ['Artisan', '/collections/furniture-living-room-furniture/dovetail'], ['Mobital', '/collections/furniture-living-room-furniture/mobital']]],
    ],
    'outdoor-furniture': [
      ['type of product', true, [['Outdoor Sofas & Sectionals', '/collections/outdoor-sofas-sectionals'], ['Outdoor Accent Chairs', '/collections/outdoor-accent-chairs'], ['Outdoor Dining Tables', '/collections/outdoor-dining-tables'], ['Outdoor Coffee Tables', '/collections/outdoor-coffee-tables'], ['Outdoor Benches & Ottomans', '/collections/outdoor-benches-stools-ottomans'], ['Outdoor Rugs', '/collections/outdoor-rugs'], ['Outdoor Pillows & Cushions', '/collections/outdoor-pillows-cushions'], ['Outdoor Accessories', '/collections/outdoor-accessories'], ['Outdoor Lighting', '/collections/outdoor-lighting']]],
      ['material', false, [['Teak', '/collections/outdoor-furniture/teak'], ['Aluminum', '/collections/outdoor-furniture/aluminum'], ['Wicker', '/collections/outdoor-furniture/wicker'], ['Concrete', '/collections/outdoor-furniture/concrete'], ['Performance Fabric', '/collections/outdoor-furniture/performance-fabric']]],
      ['brand', false, [['Universal', '/collections/outdoor-furniture/universal'], ['Classic Home', '/collections/outdoor-furniture/classic-home'], ['Four Hands', '/collections/outdoor-furniture/four-hands'], ['Artisan', '/collections/outdoor-furniture/dovetail'], ['Uttermost', '/collections/outdoor-furniture/uttermost']]],
      ['use case', false, [['Outdoor Dining', '/collections/outdoor-dining-tables'], ['Outdoor Lounge', '/collections/outdoor-sofas-sectionals'], ['Poolside', '/collections/outdoor-furniture/poolside'], ['Front Porch', '/collections/outdoor-furniture/front-porch'], ['Outdoor Entertaining', '/collections/outdoor-furniture/outdoor-entertaining']]],
    ],
    rugs: [
      ['type of product', true, [['Hand-Knotted Rugs', '/collections/hand-knotted'], ['Hand-Tufted Rugs', '/collections/hand-tufted'], ['Power-Loomed Rugs', '/collections/power-loomed-1'], ['Turkish Patchwork Rugs', '/collections/turkish-patchwork-rugs'], ['Outdoor Rugs', '/collections/outdoor-rugs']]],
      ['room', false, [['Living Room', '/collections/rugs/living-room'], ['Dining Room', '/collections/rugs/dining-room'], ['Bedroom', '/collections/rugs/bedroom'], ['Outdoor', '/collections/rugs/outdoor']]],
      ['style', false, [['Modern', '/collections/rugs/modern'], ['Vintage', '/collections/rugs/vintage'], ['Neutral', '/collections/rugs/neutral'], ['Wool', '/collections/rugs/wool'], ['Jute', '/collections/rugs/jute']]],
      ['size', false, [['5 x 8', '/collections/rugs/5x8'], ['8 x 10', '/collections/rugs/8x10'], ['9 x 12', '/collections/rugs/9x12'], ['Runner', '/collections/rugs/runner'], ['Oversized', '/collections/rugs/oversized']]],
    ],
    lighting: [
      ['type of product', true, [['Table Lamps', '/collections/lighting-table-lamps'], ['Floor Lamps', '/collections/lighting-floor-lamps'], ['Chandeliers', '/collections/lighting-chandeliers'], ['Pendants', '/collections/lighting-pendants'], ['Wall Sconces', '/collections/lighting-wall-sconces'], ['Flush Mounts', '/collections/lighting-flush-mounts'], ['Buffet Lamps', '/collections/lighting-buffet-lamps'], ['Lanterns', '/collections/lighting-lanterns'], ['Outdoor Lighting', '/collections/outdoor-lighting']]],
      ['finish', false, [['Brass', '/collections/lighting/brass'], ['Bronze', '/collections/lighting/bronze'], ['Black', '/collections/lighting/black'], ['White', '/collections/lighting/white'], ['Glass', '/collections/lighting/glass'], ['Ceramic', '/collections/lighting/ceramic']]],
      ['brand', false, [['Arteriors', '/collections/lighting/arteriors'], ['Uttermost', '/collections/lighting/uttermost'], ['Mercana', '/collections/lighting/mercana'], ['Four Hands', '/collections/lighting/four-hands']]],
      ['style', false, [['Modern', '/collections/lighting/modern'], ['Traditional', '/collections/lighting/traditional'], ['Transitional', '/collections/lighting/transitional'], ['Industrial', '/collections/lighting/industrial'], ['Coastal', '/collections/lighting/coastal']]],
    ],
    'home-decor': [
      ['type of product', true, [['Wall Art', '/collections/decor-wall-art'], ['Wall Decor & Mirrors', '/collections/decor-wall-decor-mirrors'], ['Vases & Jars', '/collections/decor-vases-jars'], ['Pillows & Throws', '/collections/decor-pillows-throws'], ['Bowls & Trays', '/collections/decor-bowls-trays'], ['Sculptures & Objects', '/collections/decor-sculptures-objects'], ['Baskets & Storage', '/collections/decor-baskets-storage'], ['Planters & Botanicals', '/collections/decor-planters-botanicals'], ['Pets', '/collections/decor-pets']]],
      ['material', false, [['Ceramic', '/collections/home-decor/ceramic'], ['Glass', '/collections/home-decor/glass'], ['Stone', '/collections/home-decor/stone'], ['Wood', '/collections/home-decor/wood'], ['Metal', '/collections/home-decor/metal'], ['Textile', '/collections/home-decor/textile']]],
      ['brand', false, [['Uttermost', '/collections/home-decor/uttermost'], ['Mercana', '/collections/home-decor/mercana'], ['Classic Home', '/collections/home-decor/classic-home'], ['Four Hands', '/collections/home-decor/four-hands']]],
      ['style', false, [['Modern', '/collections/home-decor/modern'], ['Traditional', '/collections/home-decor/traditional'], ['Coastal', '/collections/home-decor/coastal'], ['Rustic', '/collections/home-decor/rustic'], ['Organic', '/collections/home-decor/organic']]],
    ],
    'shop-whats-new': [
      ['type of product', true, [['Sofas', '/collections/shop-whats-new/sofa'], ['Dining Tables', '/collections/shop-whats-new/dining-table'], ['Chairs', '/collections/shop-whats-new/chair'], ['Cabinets', '/collections/shop-whats-new/cabinet'], ['Beds', '/collections/shop-whats-new/bed'], ['Lighting', '/collections/shop-whats-new/lighting'], ['Decor', '/collections/shop-whats-new/decor'], ['Outdoor', '/collections/shop-whats-new/outdoor']]],
      ['room', false, [['Living Room', '/collections/shop-whats-new/living-room'], ['Dining Room', '/collections/shop-whats-new/dining-room'], ['Bedroom', '/collections/shop-whats-new/bedroom'], ['Outdoor', '/collections/shop-whats-new/outdoor']]],
      ['brand', false, [['Universal', '/collections/shop-whats-new/universal'], ['Classic Home', '/collections/shop-whats-new/classic-home'], ['Uttermost', '/collections/shop-whats-new/uttermost'], ['Artisan', '/collections/shop-whats-new/dovetail'], ['Mobital', '/collections/shop-whats-new/mobital']]],
      ['style', false, [['Modern', '/collections/shop-whats-new/modern'], ['Rustic', '/collections/shop-whats-new/rustic'], ['Coastal', '/collections/shop-whats-new/coastal'], ['Industrial', '/collections/shop-whats-new/industrial'], ['Traditional', '/collections/shop-whats-new/traditional']]],
    ],
  };

  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const currentCollectionHandle = () => {
    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]).toLowerCase() : '';
  };

  const currentTagHandle = (collectionHandle) => {
    const match = window.location.pathname.match(new RegExp(`/collections/${collectionHandle}/([^/?#]+)`));
    return match ? decodeURIComponent(match[1]).toLowerCase() : '';
  };

  const itemMarkup = ([label, href], activeTag, collectionHandle) => {
    const itemHandle = href.split('/').filter(Boolean).pop();
    const isActive = window.location.pathname === href || itemHandle === activeTag;
    const basePath = `/collections/${collectionHandle}`;
    const finalHref = isActive && href.startsWith(`${basePath}/`) ? basePath : href;
    return `
      <li>
        <a class="zin-main-fallback-facets__link zin-onsale-fallback-facets__link${isActive ? ' is-active' : ''}" href="${finalHref}"${isActive ? ' aria-current="page"' : ''}>
          <span class="zin-main-fallback-facets__box zin-onsale-fallback-facets__box" aria-hidden="true"></span>
          <span class="reversed-link__text">${escapeHtml(label)}</span>
        </a>
      </li>`;
  };

  const groupMarkup = ([title, open, items], activeTag, collectionHandle) => `
    <details class="accordion-details facet-accordion-details"${open || items.some((item) => item[1].split('/').filter(Boolean).pop() === activeTag) ? ' open' : ''}>
      <summary class="accordion-details__summary flex items-center justify-between">
        <span class="h6">${escapeHtml(title)}</span>
        <span aria-hidden="true">+</span>
      </summary>
      <div class="accordion-details__content parent-display flex flex-col gap-3">
        <ul class="grid gap-3">${items.map((item) => itemMarkup(item, activeTag, collectionHandle)).join('')}</ul>
      </div>
    </details>`;

  const initZinMainCollectionFallback = () => {
    const collection = document.querySelector('.collection--clearance-layout');
    const target = document.querySelector('#FacetFiltersContainer .sticky-element');
    const collectionHandle = collection?.className.match(/collection--([a-z0-9-]+)/)?.[1] || currentCollectionHandle();
    const groups = departmentFallbacks[collectionHandle];
    if (!collection || !target) return;
    if (!groups) return;
    if (target.querySelector('.zin-onsale-fallback-facets')) return;
    if (target.querySelector('.face-form, .facet-accordion-details')) return;

    target.insertAdjacentHTML(
      'afterbegin',
      `<nav class="face-form zin-main-fallback-facets zin-onsale-fallback-facets" aria-label="Collection filters" data-zin-main-fallback-collection="${collectionHandle}">${groups
        .map((group) => groupMarkup(group, currentTagHandle(collectionHandle), collectionHandle))
        .join('')}</nav>`
    );
  };

  const normalizeMobileFallbackAccordions = () => {
    if (!window.matchMedia('(max-width: 1023px)').matches) return;

    document.querySelectorAll('.collection--clearance-layout .zin-main-fallback-facets details[open]').forEach((details) => {
      if (!details.querySelector('.is-active, [aria-current="page"]')) {
        details.removeAttribute('open');
      }
    });
  };

  const getZinMobileFacetBackdrop = () => {
    let backdrop = document.querySelector('[data-zin-mobile-facet-backdrop]');

    if (!backdrop) {
      backdrop = document.createElement('button');
      backdrop.type = 'button';
      backdrop.className = 'zin-mobile-facet-backdrop';
      backdrop.setAttribute('aria-label', 'Close filters');
      backdrop.setAttribute('data-zin-mobile-facet-backdrop', '');
      backdrop.hidden = true;
      document.body.appendChild(backdrop);
    }

    return backdrop;
  };

  const ensureZinMobileFacetDrawerChrome = (panel) => {
    const target = panel.querySelector('.sticky-element') || panel;

    if (!target.querySelector('[data-zin-mobile-facet-header]')) {
      target.insertAdjacentHTML(
        'afterbegin',
        `<div class="zin-mobile-facet-drawer__header" data-zin-mobile-facet-header>
          <div class="zin-mobile-facet-drawer__title">
            <span class="zin-mobile-facet-drawer__icon" aria-hidden="true"><span></span><span></span><span></span></span>
            <span>Filters</span>
          </div>
          <button type="button" class="zin-mobile-facet-drawer__close" data-zin-mobile-filter-close aria-label="Close filters"></button>
        </div>`
      );
    }

    if (!target.querySelector('[data-zin-mobile-facet-footer]')) {
      target.insertAdjacentHTML(
        'beforeend',
        `<div class="zin-mobile-facet-drawer__footer" data-zin-mobile-facet-footer>
          <button type="button" class="zin-mobile-facet-drawer__view" data-zin-mobile-filter-close>View items</button>
        </div>`
      );
    }
  };

  const initZinMobileCollectionControls = () => {
    const button = document.querySelector('[data-zin-mobile-filter-toggle]');
    const panel = document.getElementById('FacetFiltersContainer');
    if (!button || !panel || button.dataset.zinMobileFilterBound === 'true') return;

    const backdrop = getZinMobileFacetBackdrop();
    ensureZinMobileFacetDrawerChrome(panel);
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Collection filters');

    const setOpen = (open) => {
      ensureZinMobileFacetDrawerChrome(panel);
      panel.classList.toggle('is-mobile-open', open);
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      backdrop.hidden = !open;
      document.documentElement.classList.toggle('zin-facets-lock', open);
    };

    setOpen(panel.classList.contains('is-mobile-open'));
    button.dataset.zinMobileFilterBound = 'true';
    button.addEventListener('click', () => {
      setOpen(!panel.classList.contains('is-mobile-open'));
    });
    backdrop.addEventListener('click', () => setOpen(false));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setOpen(false);
    });
    panel.addEventListener('click', (event) => {
      if (event.target.closest('[data-zin-mobile-filter-close]')) {
        setOpen(false);
      }

      if (window.matchMedia('(max-width: 1023px)').matches && event.target.closest('a')) {
        setOpen(false);
      }
    });

    document.addEventListener('collection:rerendered', () => {
      ensureZinMobileFacetDrawerChrome(panel);
      setOpen(panel.classList.contains('is-mobile-open'));
    });
  };

  const initZinMainCollectionFallbacks = () => {
    initZinMainCollectionFallback();
    normalizeMobileFallbackAccordions();
    initZinMobileCollectionControls();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initZinMainCollectionFallbacks);
  } else {
    initZinMainCollectionFallbacks();
  }
  document.addEventListener('shopify:section:load', initZinMainCollectionFallbacks);
})();
